<template>
    <div class="event-card full-width q-px-sm q-py-md">
        <q-list separator>
            <q-collapsible group="costs" icon="fal fa-money-bill-wave" :label="$t('totals')" opened>
                <q-table
                    :data="total_costs"
                    :columns="columns_total_costs"
                    row-key="name"
                    :pagination.sync="server_pagination"
                    :no-data-label="$t('no_data_avaliable')"
                    :no-results-label="$t('no_results_found')"
                    :loading-label="$t('loading_data')"
                    :rows-per-page-label="$t('items_per_page')"
                    :rows-per-page-options="[0]"
                    class="q-my-md"
                    binary-state-sort
                    hide-bottom
                    >

                    <q-tr slot="header" slot-scope="props" :props="props">
                        <q-th v-for="col in props.cols" :key="col.name" :props="props">
                            {{ $t(col.label) }}
                        </q-th>
                    </q-tr>

                    <q-tr slot="body" slot-scope="props">
                        <q-td key="name" :props="props">
                            {{ $t(props.row.name) }}
                        </q-td>

                        <q-td v-if="props.row.name === 'participants'" key="expected_total" :props="props">
                            {{ props.row.expected_total }}
                        </q-td>

                        <q-td v-else key="expected_total" :props="props">
                            $ {{ props.row.expected_total }}
                        </q-td>

                        <q-td v-if="props.row.editable" key="actual_total" :props="props">
                            <q-input type="number" prefix="$" v-model="props.row.actual_total" @input="calculateCosts" />
                        </q-td>
                        <q-td v-else key="actual_total" :props="props">
                            {{ props.row.name === 'participants' ? props.row.actual_total : `$${props.row.actual_total}` }}
                        </q-td>

                    </q-tr>
                </q-table>
            </q-collapsible>

            <q-collapsible group="costs" icon="fal fa-box-usd" :label="$t('materials')">
                <q-table
                    :data="materials"
                    :columns="columns_materials"
                    :pagination.sync="server_pagination"
                    row-key="material_id"
                    :no-data-label="$t('no_data_avaliable')"
                    :no-results-label="$t('no_results_found')"
                    :loading-label="$t('loading_data')"
                    :rows-per-page-label="$t('items_per_page')"
                    :rows-per-page-options="[0]"
                    class="q-my-md"
                    binary-state-sort
                    >

                    <q-tr slot="header" slot-scope="props" :props="props">
                        <q-th v-for="col in props.cols" :key="col.name" :props="props">
                            {{ $t(col.label) }}
                        </q-th>
                    </q-tr>

                    <q-tr slot="body" slot-scope="props">
                        <q-td key="material" :props="props">
                            {{ props.row.material }}
                        </q-td>

                        <q-td key="qty_material" :props="props">
                            <q-input type="number" v-model="props.row.qty_material" readonly />
                        </q-td>

                        <q-td key="unit_material_cost" :props="props">
                            $ {{ props.row.unit_material_cost }}
                        </q-td>

                        <q-td key="total_material_cost" :props="props">
                            $ {{ props.row.total_material_cost.toFixed(2) }}
                        </q-td>
                    </q-tr>
                </q-table>
            </q-collapsible>

            <q-collapsible group="costs" icon="fal fa-money-check-edit" :label="$t('additional_costs')">
                <q-table
                    :data="additional_costs"
                    :columns="columns_additional_costs"
                    :pagination.sync="server_pagination"
                    row-key="cost_id"
                    :no-data-label="$t('no_data_avaliable')"
                    :no-results-label="$t('no_results_found')"
                    :loading-label="$t('loading_data')"
                    :rows-per-page-label="$t('items_per_page')"
                    :rows-per-page-options="[0]"
                    class="q-my-md"
                    binary-state-sort
                    >

                    <template slot="top" slot-scope="props">
                        <div class="col" />
                        <q-btn color="secondary" icon="far fa-plus" :label="$t('add')" @click="show_add_costs = true" :disable="disableAddCosts" />
                    </template>

                    <q-tr slot="header" slot-scope="props" :props="props">
                        <q-th v-for="col in props.cols" :key="col.name" :props="props">
                            {{ $t(col.label) }}
                        </q-th>
                    </q-tr>

                    <q-tr slot="body" slot-scope="props">
                        <q-td key="description" :props="props">
                            {{ props.row.description }}
                        </q-td>

                        <q-td key="qty_additional_cost" :props="props">
                            <q-input type="number" v-model="props.row.qty_additional_cost" readonly />
                        </q-td>

                        <q-td key="unit_additional_cost" :props="props">
                            $ {{ props.row.unit_additional_cost.toFixed(2) }}
                        </q-td>

                        <q-td key="total_additional_cost" :props="props">
                            $ {{ props.row.total_additional_cost.toFixed(2) }}
                        </q-td>

                        <q-td auto-width key="actions" :props="props">
                            <q-btn round flat icon="fal fa-pen" color="info" @click="editAdditionalCost(props.row)" />
                            <q-btn round flat icon="fal fa-trash" color="negative" @click="removeAdditionalCost(props.row.id)" />
                        </q-td>
                    </q-tr>
                </q-table>
            </q-collapsible>
        </q-list>

        <div class="row gutter-sm justify-end q-mt-lg">
            <div class="col-xs-12 col-sm-3">
                <q-btn color="secondary" class="full-width" :label="$t('save_costs')" icon="far fa-save" @click="saveTotalCosts()" />
            </div>
        </div>

        <q-modal v-model="show_add_costs" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh'}" @hide="clearNewCost()" @escape-key="clearNewCost()">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="show_add_costs = false" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('include_additional_cost') }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="col-12">
                        <q-field :count="256">
                            <q-input v-model="new_cost_description" type="text" :float-label="$t('description')" />
                        </q-field>
                    </div>
                    <div class="col-12">
                        <q-input v-model="new_cost_qty" @input="calcNewCostTotal" type="number" :float-label="$t('quantity')" />
                    </div>
                    <div class="col-12">
                        <q-input v-model="new_cost_unit_cost" @input="calcNewCostTotal" type="number" :float-label="$t('unitary_value')" prefix="$" />
                    </div>
                    <div class="col-12">
                        <q-input v-model="new_cost_total_cost" type="number" :float-label="$t('total_cost')" readonly prefix="$"/>
                    </div>
                </div>

                <div class="row gutter-sm justify-end q-mt-sm q-mx-sm layout-padding">
                    <div class="col-xs-12 col-sm-3 q-mx-xs">
                        <q-btn color="tertiary" class="full-width" :label="$t('cancel')" icon="far fa-chevron-left" @click="clearNewCost()" />
                    </div>

                    <div class="col-xs-12 col-sm-3 q-mx-xs">
                        <q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveNewCost()" :disable="new_cost_total_cost === 0 ? true : false" />
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>

        <q-modal v-model="show_edit_cost" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh'}" @hide="clearNewCost()" @escape-key="clearNewCost()">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="show_edit_cost = false" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('edit_cost') }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="col-12">
                        <q-field :count="256">
                            <q-input v-model="new_cost_description" type="text" :float-label="$t('description')" />
                        </q-field>
                    </div>
                    <div class="col-12">
                        <q-input v-model="new_cost_qty" @input="calcNewCostTotal" type="number" :float-label="$t('quantity')" />
                    </div>
                    <div class="col-12">
                        <q-input v-model="new_cost_unit_cost" @input="calcNewCostTotal" type="number" :float-label="$t('unitary_value')" prefix="$" />
                    </div>
                    <div class="col-12">
                        <q-input v-model="new_cost_total_cost" type="number" :float-label="$t('total_cost')" readonly prefix="$"/>
                    </div>
                </div>

                <div class="row gutter-sm justify-end q-mt-sm q-mx-sm layout-padding">
                    <div class="col-xs-12 col-sm-3 q-mx-xs">
                        <q-btn color="tertiary" class="full-width" :label="$t('cancel')" icon="far fa-chevron-left" @click="clearNewCost()" />
                    </div>

                    <div class="col-xs-12 col-sm-3 q-mx-xs">
                        <q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="updateCost(edit_cost_id)" :disable="new_cost_total_cost === 0 ? true : false" />
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>

    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { date, debounce } from 'quasar'
const { formatDate } = date

export default {
    name: 'AdminPresentialCosts',

    props: ['status', 'is_training_manager'],

    data() {
        return {
            presential_event_id: '',
            tab_results: '0',
            show_add_costs: false,
            show_edit_cost: false,
            edit_cost_id: null,
            costs_real: [],
            total_costs: [
                {
                    name: 'participants',
                    expected_total: 0,
                    actual_total: 0,
                    editable: false
                },
                {
                    name: 'value_per_participant',
                    expected_total: 0,
                    actual_total: 0,
                    editable: false
                },
                {
                    name: 'total_value_participants',
                    expected_total: 0,
                    actual_total: 0,
                    editable: true
                },
                {
                    name: 'total_value_materials',
                    expected_total: 0,
                    actual_total: 0,
                    editable: true
                },
                {
                    name: 'total_additional_cost',
                    expected_total: 0,
                    actual_total: 0,
                    editable: true
                },
                {
                    name: 'general_total',
                    expected_total: 0,
                    actual_total: 0,
                    editable: false
                },
            ],
            columns_total_costs: [
                {
                    name: 'name',
                    required: true,
                    label: '',
                    align: 'left',
                    field: 'name',
                    sortable: false,
                },
                {
                    name: 'expected_total',
                    required: true,
                    label: 'expected_total',
                    align: 'left',
                    field: 'expected_total',
                    sortable: false,
                },
                {
                    name: 'actual_total',
                    required: true,
                    label: 'actual_total',
                    align: 'left',
                    field: 'actual_total',
                    sortable: false,
                },
            ],
            materials: [],
            columns_materials: [
                {
                    name: 'material',
                    required: true,
                    label: 'material',
                    align: 'left',
                    field: 'material',
                    sortable: false,
                },
                {
                    name: 'qty_material',
                    required: true,
                    label: 'quantity',
                    align: 'left',
                    field: 'qty_material',
                    sortable: false,
                },
                {
                    name: 'unit_material_cost',
                    required: true,
                    label: 'unitary_value',
                    align: 'left',
                    field: 'unit_material_cost',
                    sortable: false,
                },
                {
                    name: 'total_material_cost',
                    required: true,
                    label: 'total_cost',
                    align: 'left',
                    field: 'total_material_cost',
                    sortable: false,
                }
            ],
            additional_costs: [],
            additional_cost_status: 1,
            columns_additional_costs: [
                {
                    name: 'description',
                    required: true,
                    label: 'description',
                    align: 'left',
                    field: 'description',
                    sortable: false,
                },
                {
                    name: 'qty_additional_cost',
                    required: true,
                    label: 'quantity',
                    align: 'left',
                    field: 'qty_additional_cost',
                    sortable: false,
                },
                {
                    name: 'unit_additional_cost',
                    required: true,
                    label: 'unitary_value',
                    align: 'left',
                    field: 'unit_material_cost',
                    sortable: false,
                },
                {
                    name: 'total_additional_cost',
                    required: true,
                    label: 'total_cost',
                    align: 'left',
                    field: 'total_additional_cost',
                    sortable: false,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'actions',
                    sortable: false,
                }
            ],
            new_cost_description: '',
            new_cost_qty: 0,
            new_cost_unit_cost: 0,
            new_cost_total_cost: 0,
            server_pagination: {
                page: 1,
                rowsPerPage: 0,
                descending: false,
                sortBy: null
            }
        }
    },

    computed: {
		...mapGetters({
            access_token: 'common/access_token',
            id_user_language_selected: 'common/id_user_language_selected'
		}),

		getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '90vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        },

        calculateCosts() {
            if (this.total_costs[0].actual_total === 0) {
                this.total_costs[0].actual_total = 0
            } else {
                this.total_costs[1].actual_total = (Number(this.total_costs[2].actual_total) / Number(this.total_costs[0].actual_total)).toFixed(2)
            }
            this.total_costs[5].actual_total = (Number(this.total_costs[2].actual_total) + Number(this.total_costs[3].actual_total) + Number(this.total_costs[4].actual_total)).toFixed(2)
        },

        calcNewCostTotal() {
            this.new_cost_total_cost = this.new_cost_qty * this.new_cost_unit_cost
        },

        disableAddCosts() {
            if (this.status === 8 && !this.is_training_manager) return true
            else return false
        }
    },

    methods: {
        requestCosts() {
            this.$q.loading.show()

            this.$axios.get(`/presential/presential_events/${this.presential_event_id}/costs/`, this.getHeader)
            .then(response => {
                let d = response.data

                this.additional_costs = d.additional_costs
                this.materials = d.materials
                this.costs_real = d.costs_real ? d.costs_real
                : {
                    'value_real_per_participant': 0,
                    'total_real_additional_cost': 0,
                    'total_real_value_participants': 0,
                    'total_real_value_materials': 0,
                    'total_real_value_event': 0
                }

                this.total_costs[0].expected_total = d.total_expected_participants
                this.total_costs[0].actual_total = d.total_real_participants
                this.total_costs[1].expected_total = d.value_per_participant.toFixed(2)
                this.total_costs[1].actual_total = this.costs_real.value_real_per_participant.toFixed(2)
                this.total_costs[2].expected_total = d.total_value_participants.toFixed(2)
                this.total_costs[2].actual_total = this.costs_real.total_real_value_participants.toFixed(2)
                this.total_costs[3].expected_total = d.total_value_materials.toFixed(2)
                this.total_costs[3].actual_total = this.costs_real.total_real_value_materials.toFixed(2)
                this.total_costs[4].expected_total = d.total_additional_cost.toFixed(2)
                this.total_costs[4].actual_total = this.costs_real.total_real_additional_cost.toFixed(2)
                this.total_costs[5].expected_total = d.total_value_event.toFixed(2)
                this.total_costs[5].actual_total = this.costs_real.total_real_value_event.toFixed(2)


                this.additional_costs.map(e => {
                    return { ...e, actions: e.id}
                })

            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        saveTotalCosts() {
            this.$q.loading.show()

            let data = {
                total_real_participants: Number(this.total_costs[0].actual_total),
                value_real_per_participant: Number(this.total_costs[1].actual_total),
                total_real_value_participants: Number(this.total_costs[2].actual_total),
                total_real_value_materials: Number(this.total_costs[3].actual_total),
                total_real_additional_cost: Number(this.total_costs[4].actual_total),
                total_real_value_event: Number(this.total_costs[5].actual_total),
            }

            if (this.costs_real.id_costs_real) {
                this.$axios.put(`/presential/presential_events/${this.presential_event_id}/costs/update_costs_real/`, data, this.getHeader)
                .then(response => {
                    this.$q.notify({
                        message: this.$t('training_costs_edit_success'),
                        type: 'positive',
                        icon: 'fal fa-save'
                    })
                })
                .catch(e => {
                    console.log(e)
                    this.$q.loading.hide()
                    this.show_add_costs = false

                    this.$q.notify({
                        message: this.$t('training_costs_edit_error'),
                        type: 'negative',
                        icon: 'fal fa-ban'
                    })
                })
                .finally(() => {
                    this.requestCosts()
                })
            } else {
                this.$axios.post(`/presential/presential_events/${this.presential_event_id}/costs/set_costs_real/`, data, this.getHeader)
                .then(response => {
                    this.$q.notify({
                        message: this.$t('training_costs_save_success'),
                        type: 'positive',
                        icon: 'fal fa-save'
                    })
                })
                .catch(e => {
                    console.log(e)
                    this.$q.loading.hide()
                    this.show_add_costs = false

                    this.$q.notify({
                        message: this.$t('training_costs_save_error'),
                        type: 'negative',
                        icon: 'fal fa-ban'
                    })
                })
                .finally(() => {
                    this.requestCosts()
                })
            }
        },

        saveNewCost() {
            this.$q.loading.show()

            let data = {
                description: this.new_cost_description,
                cost_value: this.new_cost_unit_cost,
                qty_additional_cost: this.new_cost_qty,
                status: this.additional_cost_status
            }

            this.$axios.post(`/presential/presential_events/${this.presential_event_id}/costs/`, data, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('additional_cost_save_success'),
                    type: 'positive',
                    icon: 'fal fa-save'
                })
            })
            .catch(e => {
                console.log(e)
                this.$q.loading.hide()
                this.show_add_costs = false

                this.$q.notify({
                    message: this.$t('additional_cost_save_error'),
                    type: 'negative',
                    icon: 'fal fa-ban'
                })
            })
            .finally(() => {
                this.clearNewCost()
                this.requestCosts()
            })
        },

        removeAdditionalCost(id) {
            this.$q.loading.show()

            this.$axios.delete(`/presential/presential_events/${this.presential_event_id}/costs/${id}/`, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('additional_cost_remove_success'),
                    type: 'positive',
                    icon: 'fal fa-save'
                })
            })
            .catch(e => {
                console.log(e)
                this.$q.loading.hide()

                this.$q.notify({
                    message: this.$t('additional_cost_remove_error'),
                    type: 'negative',
                    icon: 'fal fa-ban'
                })
            })
            .finally(() => {
                this.requestCosts()
            })
        },

        editAdditionalCost(cost) {
            this.show_edit_cost = true
            this.new_cost_description = cost.description
            this.new_cost_unit_cost = cost.unit_additional_cost
            this.new_cost_qty = cost.qty_additional_cost
            this.new_cost_total_cost = cost.total_value_additional_cost
            this.edit_cost_id = cost.id
        },

        updateCost(id) {
            this.$q.loading.show()

            let data = {
                description: this.new_cost_description,
                cost_value: this.new_cost_unit_cost,
                qty_additional_cost: this.new_cost_qty,
                status: this.additional_cost_status
            }

            this.$axios.put(`/presential/presential_events/${this.presential_event_id}/costs/${id}/`, data, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('additional_cost_edit_success'),
                    type: 'positive',
                    icon: 'fal fa-save'
                })
            })
            .catch(e => {
                console.log(e)
                this.$q.loading.hide()
                this.show_edit_cost = false

                this.$q.notify({
                    message: this.$t('additional_cost_edit_error'),
                    type: 'negative',
                    icon: 'fal fa-ban'
                })
            })
            .finally(() => {
                this.clearNewCost()
                this.requestCosts()
            })
        },

        clearNewCost() {
            this.show_add_costs = false
            this.show_edit_cost = false
            this.new_cost_description = '',
            this.new_cost_qty = 0,
            this.new_cost_unit_cost = 0,
            this.new_cost_total_cost = 0
        },

        fDate(d) {
            if (d) {
                let format = ''

                switch(this.id_user_language_selected) {
                    case 1:
                    case 3: format = 'DD/MM/YYYY HH:mm:ss'
                    break
                    default: format = 'MM-DD-YYYY HH:mm:ss'
                    break
                }

                return date.formatDate(d, format)
            }
        },
    },

    created() {
        this.presential_event_id = this.$route.params.id

        this.requestCosts()
    },
}
</script>

<style lang="stylus" scoped>
.event-card
    box-shadow 0 1px 5px rgba(0,0,0,0.2), 0 2px 2px rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12)

</style>
